import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import { getFormattedUnit } from "config";

// Define styles
const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    paddingTop: 40, // Space for header
    paddingBottom: 80, // Space for footer
    paddingHorizontal: 30,
  },
  header: {
    position: "absolute",
    top: 20,
    left: 30,
    right: 30,
    textAlign: "center",
    fontSize: 12,
    fontWeight: "bold",
    fontFamily: "Helvetica-Bold",
  },

  headerBlock: {
    position: "absolute",
    top: 20,
    left: 30,
    right: 30,
    textAlign: "center",
    alignItems: "center",
  },

  headerText: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    marginBottom: 2, // slight spacing between lines
  },
  companyAddress: {
    position: "absolute",
    top: 35,
    left: 30,
    right: 30,
    textAlign: "center",
    fontSize: 10,
  },
  section: {
    marginBottom: 20,
  },
  sectionHeader: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 10,
    textAlign: "left",
    fontFamily: "Helvetica-Bold",
  },
  mainHeader: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 10,
    textAlign: "center",
    textDecoration: "underline",
    fontFamily: "Helvetica-Bold",
  },
  supplierDetails: {
    fontSize: 10,
    marginBottom: 10,
    width: "45%",
  },
  poDetails: {
    fontSize: 10,
    marginBottom: 10,
    width: "55%",
  },
  table: {
    display: "table",
    width: "100%",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#000",
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderColor: "#000",
  },
  tableCell: {
    padding: 5,
    fontSize: 10,
    borderRightWidth: 1,
    borderRightStyle: "solid",
    borderColor: "#000",
    // prevent awkward mid-word hyphenation and allow proper wrapping
    flexWrap: "wrap",
    minWidth: 0,
    wordBreak: "normal",
    overflowWrap: "normal",
  },
  tableCellLast: {
    borderRightWidth: 0,
  },
  bold: {
    fontWeight: "bold",
    fontFamily: "Helvetica-Bold",
  },
  text: {
    fontSize: 10,
    marginBottom: 5,
    // ensure text wraps nicely and doesn't hyphenate mid-word
    flexWrap: "wrap",
    minWidth: 0,
    wordBreak: "normal",
    overflowWrap: "normal",
  },
  totalSection: {
    flexDirection: "row",
    marginTop: 0,
    fontSize: 10,
    marginBottom: 10,
  },
  leftSection: {
    width: "50%",
  },
  rightSection: {
    width: "50%",
    textAlign: "right",
  },
  termsAndConditions: {
    flexDirection: "row",
    justifyContent: "space-between",
    // marginTop: 20,
    fontSize: 10,
    // borderBottomWidth: 1,
    borderBottomStyle: "solid",
    borderColor: "#000",
  },
  termsLeft: {
    width: "48%",
    borderRightWidth: 1,
    borderRightStyle: "solid",
    borderColor: "#000",
    paddingTop: 5,
  },
  termsRight: {
    width: "48%",
    paddingLeft: 0,
    paddingTop: 5,
  },
  signatureImage: {
    height: 60,
    width: 70,
    marginTop: 5,
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    fontSize: 8,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  contentBorder: {
    borderWidth: 1,
    borderColor: "#000",
    padding: 10,
    marginTop: 20, // Push content below header
    marginBottom: 10, // Ensure space before footer
    flexGrow: 1, // Allow border to expand to full height
  },
  topBottomBorder: {
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    paddingTop: 5,
    paddingBottom: 5,
  },
  tovSection: {
    width: "100%",
    paddingTop: 5,
    paddingBottom: 5,
    textAlign: "right",
    borderTopWidth: 1,
    borderBottomWidth: 1,
    fontSize: 10,
    fontWeight: "bold",
    borderColor: "#000",
    fontFamily: "Helvetica-Bold",
  },

  poDetailRow: {
    flexDirection: "row",
    marginBottom: 5,
    justifyContent: "flex-start", // Ensure label and value align properly to the left
    alignItems: "flex-start",
    // allow children to wrap properly
    flexWrap: "wrap",
    minWidth: 0,
  },
  poDetailLabel: {
    fontWeight: "bold",
    width: "35%", // Adjust width for label to be consistent
    marginRight: 5, // Add margin to space out from the value
    // ensure label doesn't force the value to start under the colon
    textAlign: "left",
    paddingRight: 2,
    // prevent label from shrinking unexpectedly
    minWidth: 0,
    flexShrink: 0,
  },
  poDetailValue: {
    fontWeight: "normal",
    width: "60%", // Adjust to ensure values are consistently aligned
    // allow the value to wrap and take full available width on subsequent lines
    flexWrap: "wrap",
    minWidth: 0,
    wordBreak: "normal",
    overflowWrap: "normal",
  },

  pageNumber: {
    fontSize: 8,
    top: 20,
    textAlign: "right", // Align text to the left
    width: "20%",
  },
  content: {
    flexGrow: 1, // Expandable content
  },

  poSubjectText: {
    fontSize: 10,
    fontWeight: "bold",
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
  },

  poSubjectHeadline: {
    marginBottom: 8,
    marginTop: 5,
  },
});

const stripHtml = (html) => {
  if (!html) return "N/A";

  // Decode common HTML entities
  let decoded = html
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/&quot;/g, '"');

  // Replace specific HTML tags with appropriate text format
  let text = decoded
    .replace(/<p[^>]*>/gi, "") // Remove opening <p> tags
    .replace(/<\/p[^>]*>/gi, "\n") // Replace closing </p> with newline
    .replace(/<br\s*\/?>/gi, "\n") // Replace <br> tags with newline
    .replace(/<li[^>]*>/gi, "\n• ") // List item bullet
    .replace(/<\/li>/gi, "") // Remove closing </li>
    .replace(/<[^>]+>/g, "") // Remove all remaining HTML tags
    .replace(/\r\n|\r|\n/g, "\n") // Normalize line breaks
    .replace(/[ \t]+/g, " ") // Replace tabs/multiple spaces with single space
    .replace(/\n{2,}/g, "\n") // Collapse multiple newlines
    .trim(); // Trim leading/trailing whitespace

  return text || "N/A";
};

const stripHtmlName = (html) => {
  if (!html) return "N/A";
  return (
    html
      .replace(/<\/p>\s*<p>/gi, "\n") // Convert </p><p> into line breaks
      .replace(/<\/?[^>]+(>|$)/g, "") // Remove all other HTML tags
      .trim() || "N/A"
  );
};

const PoPdfGenerator = ({ poData }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        {/* Company Header */}
        {/* <Text style={styles.header}>{poData?.buyerCompanyName}</Text>
                <Text style={styles.companyAddress}>
                    Site Address: {stripHtml(poData?.deliveryAddress)}
                </Text> */}

        <View style={styles.headerBlock}>
          <Text style={styles.headerText}>{poData?.buyerCompanyName}</Text>
          {/* {poData?.buyerCompanyGst && <Text style={styles.headerText}>GST: {poData?.buyerCompanyGst}</Text>} */}
          <Text
            style={styles.headerText}
            hyphenationCallback={(word) => [word]}
          >
            Site Address: {stripHtml(poData?.deliveryAddress)}
          </Text>
        </View>

        {/* Content with Border */}
        <View style={[styles.contentBorder, { marginTop: "30px" }]}>
          {/* Purchase Order Header */}
          <Text style={styles.mainHeader}>PURCHASE ORDER</Text>

          {/* Supplier and PO Details Side by Side */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            {/* Supplier Details */}
            <View style={[styles.supplierDetails, { width: "45%" }]}>
              <Text style={styles.bold}>Bill To,</Text>
              {"\n"}
              {"\n"}
              <Text style={styles.text}>
                <Text hyphenationCallback={(word) => [word]}>
                  {stripHtml(poData?.supplierName) || "N/A"}
                </Text>
                {"\n"}
                Address:{" "}
                <Text hyphenationCallback={(word) => [word]}>
                  {stripHtml(poData?.supplierAddress || "")}
                </Text>
                {"\n"}
                {"\n"}
                <Text style={styles.bold}>Ship To,</Text>
                {"\n"}
                <Text hyphenationCallback={(word) => [word]}>
                  {stripHtml(poData?.supplierName) || "N/A"}
                </Text>
                {"\n"}
                <Text
                  style={styles.poDetailValue}
                  hyphenationCallback={(word) => [word]}
                >
                  Address: {stripHtml(poData?.supplierAddress || "")}
                </Text>
                {"\n"}
                {"\n"}
                <Text style={styles.bold}>GST No.:</Text>{" "}
                {stripHtml(poData?.supplierGst) || ""}
                {"\n"}
                {"\n"}
                <Text style={styles.bold}>Contact Details :</Text>
                {"\n"}
                <Text style={styles.text}>
                  Contact Person: {stripHtml(poData?.contactName) || "N/A"}
                  {"\n"}
                  Contact #: {stripHtml(poData?.supplierMobile) || "N/A"}
                  {"\n"}
                  Email: {stripHtml(poData?.supplierEmail) || "N/A"}
                </Text>
              </Text>
            </View>

            {/* PO Details */}
            <View style={[styles.poDetails, { width: "55%" }]}>
              <View style={styles.poDetailRow}>
                <Text style={styles.poDetailLabel}>PO Number:</Text>
                <Text style={[styles.poDetailValue, styles.bold]}>
                  {poData?.poId || "N/A"}
                </Text>
              </View>
              <View style={styles.poDetailRow}>
                <Text style={styles.poDetailLabel}>PO Date:</Text>
                <Text style={[styles.poDetailValue, styles.bold]}>
                  {poData?.poDate || "N/A"}
                </Text>
              </View>
              <View style={styles.poDetailRow}>
                <Text style={styles.poDetailLabel}>Company Name:</Text>
                <Text style={[styles.poDetailValue, styles.bold]}
                hyphenationCallback={(word) => [word]}>
                  {poData?.buyerCompanyName || "N/A"}
                </Text>
              </View>
              <View style={styles.poDetailRow}>
                <Text style={styles.poDetailLabel}>Company GST:</Text>
                <Text style={[styles.poDetailValue, styles.bold]}>
                  {poData?.buyerCompanyGst || "N/A"}
                </Text>
              </View>
              <View style={styles.poDetailRow}>
                <Text style={styles.poDetailLabel}>Project Name:</Text>
                <Text style={[styles.poDetailValue, styles.bold]}
                hyphenationCallback={(word) => [word]}>
                  {poData?.projectName || "N/A"}
                </Text>
              </View>
              <View style={styles.poDetailRow}>
                <Text style={styles.poDetailLabel}>Delivery Address:</Text>
                <Text
                  style={[styles.poDetailValue, styles.bold]}
                  hyphenationCallback={(word) => [word]}
                >
                  {stripHtml(poData?.deliveryAddress) || "N/A"}
                </Text>
              </View>
              <View style={styles.poDetailRow}>
                <Text style={styles.poDetailLabel}>Delivery Date:</Text>
                <Text style={[styles.poDetailValue, styles.bold]}>
                  {poData?.deliveryDate
                    ? new Date(poData?.deliveryDate).toLocaleDateString("en-GB")
                    : "N/A"}
                </Text>
              </View>
              <View style={styles.poDetailRow}>
                <Text style={styles.poDetailLabel}>Site Contact Person:</Text>
                <Text style={[styles.poDetailValue, styles.bold]}>
                  {stripHtml(poData?.buyerName) || "N/A"}
                </Text>
              </View>
              <View style={styles.poDetailRow}>
                <Text style={styles.poDetailLabel}>Site Contact No.:</Text>
                <Text style={[styles.poDetailValue, styles.bold]}>
                  {stripHtml(poData?.buyerMobile) || "N/A"}
                </Text>
              </View>
              {/* <View style={styles.poDetailRow}>
                                <Text style={styles.poDetailLabel}>Email</Text>
                                <Text style={[styles.poDetailValue, styles.bold]}>: {" "} {stripHtml(poData?.buyerEmail) || "N/A"}</Text>
                            </View> */}
            </View>
          </View>

          {/* PO Subject */}
          {poData?.poSubject && (
            <View style={styles.poSubjectHeadline}>
              <Text
                style={styles.poSubjectText}
                hyphenationCallback={(word) => [word]}
              >
                {(poData?.poSubject || "")}
              </Text>
            </View>
          )}

          {/* Materials Table */}
          <View style={styles.section}>
            {/* <Text style={styles.sectionHeader}>Material Details</Text> */}
            <View
              style={[styles.table, { borderWidth: 1, borderColor: "#000" }]}
            >
              {/* Table Header */}
              <View style={[styles.tableRow, { backgroundColor: "#F5F5F5" }]}>
                <Text style={[styles.tableCell, styles.bold, { width: "8%" }]}>
                  S.No.
                </Text>
                <Text style={[styles.tableCell, styles.bold, { width: "35%" }]}>
                  Item & Description
                </Text>
                <Text style={[styles.tableCell, styles.bold, { width: "10%" }]}>
                  Unit
                </Text>
                <Text style={[styles.tableCell, styles.bold, { width: "10%" }]}>
                  Qty
                </Text>
                <Text style={[styles.tableCell, styles.bold, { width: "10%" }]}>
                  Rate
                </Text>
                <Text style={[styles.tableCell, styles.bold, { width: "10%" }]}>
                  GST
                </Text>
                <Text style={[styles.tableCell, styles.bold, { width: "17%" }]}>
                  Amount
                </Text>
              </View>

              {/* Table Rows */}
              {poData?.materials?.map((item, idx) => (
                <View key={idx} style={styles.tableRow}>
                  <Text style={[styles.tableCell, { width: "8%" }]}>
                    {idx + 1}
                  </Text>
                  <Text
                    style={[styles.tableCell, { width: "35%" }]}
                    hyphenationCallback={(word) => [word]}
                  >
                    {stripHtmlName(item.name).replace(/\n/g, "\n")}
                  </Text>

                  <Text style={[styles.tableCell, { width: "10%" }]}>
                    {getFormattedUnit(item.unit)}
                  </Text>
                  <Text style={[styles.tableCell, { width: "10%" }]}>
                    {item.quantity}
                  </Text>
                  <Text style={[styles.tableCell, { width: "10%" }]}>
                    {new Intl.NumberFormat("en-IN").format(
                      item.rate.toFixed(2)
                    )}
                  </Text>
                  <Text style={[styles.tableCell, { width: "10%" }]}>
                    {item.gst}%
                  </Text>
                  <Text style={[styles.tableCell, { width: "17%" }]}>
                    {item.totalExclGst.toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          {/* Other Charges and Totals */}
          <View style={styles.totalSection}>
            {/* Left Section: Other Charges */}
            <View style={styles.leftSection}>
              {poData?.otherCharges &&
                Object.entries(poData?.otherCharges).map(
                  ([chargeType, amount], idx) => (
                    <View style={styles.poDetailRow} key={idx}>
                      <Text style={styles.poDetailLabel}>
                        {chargeType.charAt(0).toUpperCase() +
                          chargeType.slice(1)}
                        :
                      </Text>
                      <Text style={styles.poDetailValue}>
                        <Text style={styles.bold}>
                          {amount.toLocaleString("en-IN") || 0}
                        </Text>
                      </Text>
                    </View>
                  )
                )}

              {/* Static Line for GST Applicable */}
              {/* {poData?.otherCharges && (
                                <View style={styles.poDetailRow}>
                                    <Text style={styles.poDetailLabel}>GST Applicable:</Text>
                                    <Text style={styles.poDetailValue}>
                                        <Text style={styles.bold}>:{" "}18%</Text>
                                    </Text>
                                </View>
                            )} */}
            </View>

            {/* Right Section: Totals */}
            <View style={styles.rightSection}>
              <Text style={styles.text}>
                Material Amount:{" "}
                <Text style={styles.bold}>
                  Rs.{" "}
                  {new Intl.NumberFormat("en-IN").format(
                    (poData?.materialTotalExclGST || 0).toFixed(2)
                  )}
                </Text>
              </Text>
              <Text style={styles.text}>
                Other Charges:{" "}
                <Text style={styles.bold}>
                  Rs.{" "}
                  {new Intl.NumberFormat("en-IN").format(
                    Object.values(poData?.otherCharges || {})
                      .reduce((acc, charge) => acc + charge, 0)
                      .toFixed(2)
                  )}
                </Text>
              </Text>
              <Text style={styles.text}>
                Total GST:{" "}
                <Text style={styles.bold}>
                  Rs.{" "}
                  {new Intl.NumberFormat("en-IN").format(
                    (
                      (poData?.totalInclGst || 0) - (poData?.totalExclGst || 0)
                    ).toFixed(2)
                  )}
                </Text>
              </Text>
            </View>
          </View>

          <View style={styles.tovSection}>
            <Text>
              Total Order Value (TOV):{" "}
              <Text style={styles.bold}>
                {new Intl.NumberFormat("en-IN").format(
                  poData?.totalInclGst.toFixed(2)
                ) || 0}
              </Text>
            </Text>
          </View>

          {/* Terms and Conditions and Payment Terms Side by Side */}
          <View
            style={[
              styles.termsAndConditions,
              { flexDirection: "column", paddingTop: 8, paddingBottom: 8 },
            ]}
          >
            {/* Terms & Conditions (full width) */}
            <View
              style={[
                styles.termsLeft,
                {
                  width: "100%",
                  borderRightWidth: 0,
                  borderBottomWidth: 1,
                  borderBottomStyle: "solid",
                  borderColor: "#000",
                  paddingBottom: 8,
                },
              ]}
            >
              <Text style={styles.bold}>Terms & Conditions:</Text>
              <Text style={styles.text} hyphenationCallback={(word) => [word]}>
                {stripHtml(poData?.termsAndConditions || "N/A")}
              </Text>
            </View>

            {/* Payment Terms (full width, below Terms & Conditions) */}
            <View
              style={[
                styles.termsRight,
                {
                  width: "100%",
                  paddingTop: 8,
                  paddingBottom: 8,
                  paddingLeft: 0,
                  borderTopWidth: 0,
                  borderBottomWidth: 1,
                  borderBottomStyle: "solid",
                },
              ]}
            >
              <Text style={styles.bold}>Payment Terms:</Text>
              <Text style={styles.text} hyphenationCallback={(word) => [word]}>
                {stripHtml(poData?.paymentTerms || "N/A")}
              </Text>
            </View>
            {/* Authorized Signature (placed below payment terms, aligned right) */}
            {poData?.companyStampSign && (
              <View style={{ marginTop: 12 }}>
                <Text style={styles.bold}>Authorized Signature:</Text>
                <Image
                  src={poData?.companyStampSign}
                  alt="Authorized Signature"
                  style={styles.signatureImage}
                />
              </View>
            )}
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer} fixed>
          {/* Left: User Details */}
          <View
            style={{
              width: "28%",
              textAlign: "left",
              justifyContent: "center",
              wordBreak: "break-word",
            }}
          >
            <Text>
              Prepared By:{" "}
              <Text style={styles.bold}>
                {poData?.preparedBy
                  ? poData?.preparedBy
                  : poData?.updatedBy || "N/A"}
              </Text>
            </Text>
            <Text>
              Contact No.:{" "}
              <Text style={styles.bold}>
                {stripHtml(poData?.buyerMobile) || "N/A"}
              </Text>
            </Text>
            <Text>
              Neevay Id:{" "}
              <Text style={styles.bold}>{poData?.rfqId || "N/A"}</Text>
            </Text>
          </View>

          {/* Center: Buyer Company Address */}
          <View
            style={{
              width: "60%",
              textAlign: "center",
              justifyContent: "center",
              wordBreak: "break-word",
              fontSize: 8,
              marginTop: "-5px",
            }}
          >
            <Text>Office Address : {poData?.buyerCompanyAddress || "N/A"}</Text>
          </View>

          {/* Right: Page Number */}
          <Text
            style={styles.pageNumber}
            fixed
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default PoPdfGenerator;
